import * as React from "react";
import { slugify } from "../helpers";
import useTopics from "../hooks/useTopics";
import HtmlEditor from "./HtmlEditor";
import TagEditor from "./TagEditor";
import { ArticleVisibility } from "../types";
import TopicCreateModal from "./TopicCreateModal";
import { FormGroup } from "@velaro/velaro-component-lib";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";

interface ArticleFormModel {
  visibility: ArticleVisibility;
  searchTags: string;
  content: string;
  description: string;
  topicId: string;
  slug: string;
  title: string;
  displayPriority: number;
  botSummary: string;
  enableBotResponse: boolean;
}

interface Props {
  modelState: { [key: string]: string[] };
  article: ArticleFormModel;
  onChange(fields: Partial<ArticleFormModel>): void;
}

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function generateSummary(title: string, content: string): string {
  const plainText = stripHtml(content).trim();
  if (!plainText) return "";

  // Take the first ~300 chars, break at sentence boundary
  const maxLen = 300;
  if (plainText.length <= maxLen) {
    return plainText;
  }

  const truncated = plainText.substring(0, maxLen);
  const lastSentence = truncated.lastIndexOf(".");
  if (lastSentence > 100) {
    return truncated.substring(0, lastSentence + 1);
  }

  return truncated + "...";
}

export default function ArticleForm(props: Props) {
  const [showTopicModal, setShowTopicModal] = React.useState(false);
  const { topics, addTopic } = useTopics();

  return (
    <React.Fragment>
      <FormGroup label="Title">
        <>
          <input
            type="text"
            id="title"
            className="form-control"
            autoFocus
            value={props.article.title}
            onChange={(e) => {
              props.onChange({
                title: e.currentTarget.value,
                slug: slugify(e.currentTarget.value)
              });
            }}
          />
        </>
      </FormGroup>
      <FormGroup label="Slug">
        <input
          type="text"
          id="slug"
          className="form-control"
          value={props.article.slug}
          onChange={(e) =>
            props.onChange({
              slug: e.currentTarget.value
            })
          }
        />
      </FormGroup>
      <FormGroup label="Topic">
        <>
          <div className="flex items-center justify-between mb-1">
            <div />
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setShowTopicModal(true)}
            >
              + New Topic
            </button>
          </div>
          <select
            id="topic"
            className="form-control"
            value={props.article.topicId}
            onChange={(e) => props.onChange({ topicId: e.currentTarget.value })}
          >
            <option value="">Select Topic</option>
            {(topics ?? []).map((topic) => (
              <option key={topic.Id} value={topic.Id}>
                {topic.Name}
              </option>
            ))}
          </select>
        </>
      </FormGroup>
      <FormGroup label="Description">
        <input
          type="text"
          id="description"
          className="form-control"
          value={props.article.description}
          onChange={(e) =>
            props.onChange({ description: e.currentTarget.value })
          }
        />
      </FormGroup>
      <FormGroup label="Display Priority">
        <input
          type="number"
          id="displayPriority"
          className="form-control"
          value={props.article.displayPriority}
          onChange={(e) =>
            props.onChange({ displayPriority: parseInt(e.currentTarget.value) })
          }
        />
      </FormGroup>
      <FormGroup label="Content">
        <HtmlEditor
          value={props.article.content}
          onChange={(val: string) => props.onChange({ content: val })}
        />
      </FormGroup>
      <FormGroup label="Search Tags">
        <TagEditor
          value={props.article.searchTags}
          onChange={(value) => props.onChange({ searchTags: value })}
        />
      </FormGroup>
      <FormGroup label="Visibility">
        <>
          <div className="radio">
            <label>
              <input
                type="radio"
                name="visibility"
                value={ArticleVisibility.Public}
                checked={props.article.visibility == ArticleVisibility.Public}
                onChange={(e) =>
                  props.onChange({
                    visibility: parseInt(e.currentTarget.value)
                  })
                }
              />
              Public
            </label>
          </div>
          <div className="radio">
            <label>
              <input
                type="radio"
                name="visibility"
                value={ArticleVisibility.Internal}
                checked={props.article.visibility == ArticleVisibility.Internal}
                onChange={(e) =>
                  props.onChange({
                    visibility: parseInt(e.currentTarget.value)
                  })
                }
              />
              Internal
            </label>
          </div>
        </>
      </FormGroup>

      {/* Bot Integration Section */}
      <div className="mt-6 mb-4 border border-blue-200 rounded-lg bg-blue-50/50 p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="fa fa-robot text-blue-600" />
          <h3 className="text-sm font-semibold text-blue-800">
            Bot & Agent Integration
          </h3>
        </div>
        <p className="text-xs text-blue-600 mb-3">
          Enable this article as a bot response. The bot summary is what the AI
          bot will use when responding to customer questions. Agents can also
          quickly search and send this article link during live chats.
        </p>

        <div className="mb-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={props.article.enableBotResponse ?? false}
              onChange={(e) =>
                props.onChange({ enableBotResponse: e.currentTarget.checked })
              }
              className="rounded border-slate-300"
            />
            <span className="text-sm font-medium text-slate-700">
              Enable as bot response
            </span>
          </label>
        </div>

        {props.article.enableBotResponse && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">
                Bot Summary
              </label>
              <SecondaryBlueButton
                label="Auto-Generate"
                onClick={() => {
                  const summary = generateSummary(
                    props.article.title,
                    props.article.content
                  );
                  if (summary) {
                    props.onChange({ botSummary: summary });
                  }
                }}
              />
            </div>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
              rows={4}
              placeholder="Write a concise summary that the bot will use when responding to customer questions about this topic. Keep it clear and actionable."
              value={props.article.botSummary ?? ""}
              onChange={(e) =>
                props.onChange({ botSummary: e.currentTarget.value })
              }
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-slate-400">
                {(props.article.botSummary ?? "").length} / 500 characters
              </span>
              {props.article.botSummary && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <i className="fa fa-check-circle" />
                  Ready for bot use
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {showTopicModal && (
        <TopicCreateModal
          onClose={() => setShowTopicModal(false)}
          onSubmit={(topic) => {
            addTopic(topic);
            setShowTopicModal(false);
            props.onChange({ topicId: topic.Id });
          }}
        />
      )}
    </React.Fragment>
  );
}
